/**
 * Room codes (SPEC §5.1). Four letters from an alphabet with the ambiguous
 * glyphs already removed (`ROOM_CODE_ALPHABET` excludes I and O, and there are
 * no digits at all), filtered against the profanity list.
 *
 * The room is addressed by `env.ROOM.idFromName(code)` on the worker, so a code
 * *is* the room identity — there is no separate create step to collide with.
 */
import { ROOM_CODE_ALPHABET, ROOM_CODE_LENGTH, RoomCodeSchema } from '@idgame/protocol';
import { isProfaneRoomCode } from './profanity.js';

/**
 * Ask the server for a room.
 *
 * `POST /api/rooms` mints a code, re-rolls on a collision or a blocklist hit,
 * and **claims the Durable Object** before answering — so two hosts tapping
 * "Start a room" at the same moment cannot land in each other's game. Minting a
 * code on the client instead would skip the claim and race.
 *
 * It also answers with a **host token**, which this device stores and replays in
 * `join` to claim the host seat (SPEC §5.5). Saving it is the caller's job —
 * `LandingScreen` does it the moment the room exists, before navigating, so a
 * creator who takes a minute over their selfie is still the host when they
 * arrive.
 */
export async function createRoom(signal?: AbortSignal): Promise<{
  roomCode: string;
  /** Absent from an older worker; the room then falls back to first-in. */
  hostToken?: string;
}> {
  const response = await fetch('/api/rooms', {
    method: 'POST',
    headers: { Accept: 'application/json' },
    ...(signal ? { signal } : {}),
  });

  if (!response.ok) {
    throw new Error(
      response.status === 503
        ? 'Every room code is taken right now. Try again in a moment.'
        : "We couldn't start a room. Check your connection and try again.",
    );
  }

  const body = (await response.json()) as { roomCode?: unknown; hostToken?: unknown } | null;
  const roomCode = body?.roomCode;
  if (typeof roomCode !== 'string' || !isValidRoomCode(roomCode)) {
    throw new Error("The server sent back a room code we couldn't read.");
  }
  const hostToken = body?.hostToken;
  return typeof hostToken === 'string' && hostToken.length > 0
    ? { roomCode, hostToken }
    : { roomCode };
}

function randomLetters(length: number): string {
  const bytes = new Uint8Array(length);
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < length; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  }
  let out = '';
  for (let i = 0; i < length; i += 1) {
    const byte = bytes[i] ?? 0;
    out += ROOM_CODE_ALPHABET[byte % ROOM_CODE_ALPHABET.length] ?? 'A';
  }
  return out;
}

/**
 * A locally-generated code. **Not** used to create rooms — `createRoom()` is,
 * because only the server can claim the Durable Object and rule out a
 * collision. Kept for tests and for generating throwaway codes.
 */
export function generateRoomCode(): string {
  for (let attempt = 0; attempt < 32; attempt += 1) {
    const code = randomLetters(ROOM_CODE_LENGTH);
    if (!isProfaneRoomCode(code)) return code;
  }
  // Astronomically unlikely; a deterministic fallback beats an infinite loop.
  return randomLetters(ROOM_CODE_LENGTH);
}

/** Upper-cases and strips anything outside the alphabet, for the code input. */
export function sanitiseRoomCodeInput(raw: string): string {
  const allowed = new Set(ROOM_CODE_ALPHABET.split(''));
  return raw
    .toUpperCase()
    .split('')
    .filter((char) => allowed.has(char))
    .slice(0, ROOM_CODE_LENGTH)
    .join('');
}

export function isValidRoomCode(code: string): boolean {
  return RoomCodeSchema.safeParse(code).success;
}

/** The URL a QR code encodes and the Share button shares. */
export function roomUrl(code: string, origin?: string): string {
  const base = origin ?? (typeof window === 'undefined' ? '' : window.location.origin);
  return `${base}/r/${code}`;
}
